import { apiClient } from "./client";

export interface ProfileLink {
  label: string;
  url: string;
}

export type ProfileType = "individual" | "business";

export interface AccountProfileSummary {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  profileType?: ProfileType;
}

export interface PublicUserProfile {
  userId: string;
  /** Unique handle (3-30 chars, [a-zA-Z0-9._-]). Stable identifier — what
   * appears in @mentions and profile URLs. */
  username: string;
  /** Free-form display name. Anything goes; not unique. */
  displayName: string;
  avatarUrl?: string;
  /** Profile cover/banner image URL (public B2). */
  coverImageUrl?: string;
  bio?: string;
  pronouns?: string;
  location?: string;
  links?: ProfileLink[];
  /** Customizable profile widget grid (opaque JSON). */
  profileLayout?: unknown;
  /** Profile-page theme/customization (opaque JSON). */
  profileTheme?: unknown;
  /** Villagr Eldr identity (name + appearance). Opaque JSON owned by the game client. */
  villagrEldr?: unknown;
  /** Daily-deduped profile view counter. */
  profileViewCount?: number;
  /** When true, only friends can fetch the full profile payload. */
  privateProfile?: boolean;
  /** Personal chat colour (#rrggbb) — tints this person's name in every chat
   * they appear in, for everyone. Null/absent falls back to a colour derived
   * from their user id. */
  chatColor?: string | null;
  /** Second gradient stop for the name. */
  chatColorAlt?: string | null;
  /** Name treatment: flat, gradient, glowing, or both. */
  chatNameEffect?: "none" | "gradient" | "glow" | "gradient-glow" | null;
  /** Persona kind. Defaults to "individual" for migrated accounts. */
  profileType?: ProfileType;
  createdAt: string;
  updatedAt: string;
}

export interface UserProfile extends PublicUserProfile {
  email: string;
  /** Total bytes of media this user has uploaded (drives in-app usage UI). */
  storageBytesUsed?: number;
  storageItemCount?: number;
  /** Persona this account last acted as (server-remembered). Fresh devices
   * adopt it so the X-Slumbr-Profile header is right before the first
   * data fetch. */
  lastActiveProfileId?: string;
}

export interface ProfilePageResponse extends PublicUserProfile {
  isSelf: boolean;
  isFriend: boolean;
  isFollowing: boolean;
  followingCount: number;
  followerCount: number;
  friendStatus?: FriendResponse["status"];
}

export interface ListUsersResponse {
  items: PublicUserProfile[];
}

export interface FriendResponse {
  userId: string;
  status: "pending_sent" | "pending_received" | "friends";
  createdAt: string;
  updatedAt: string;
  profile?: PublicUserProfile;
}

export interface ListFriendsResponse {
  items: FriendResponse[];
}

export interface FollowResponse {
  userId: string;
  createdAt: string;
  profile?: PublicUserProfile;
}

export interface ListFollowsResponse {
  items: FollowResponse[];
}

export async function getProfile(): Promise<UserProfile> {
  return apiClient.get<UserProfile>("/api/user/profile");
}

export async function updateProfile(
  updates: Partial<UserProfile>
): Promise<UserProfile> {
  return apiClient.put<UserProfile>("/api/user/profile", updates);
}

export async function searchUsers(query: string): Promise<ListUsersResponse> {
  const params = query.trim()
    ? `?q=${encodeURIComponent(query.trim())}`
    : "";
  return apiClient.get<ListUsersResponse>(`/api/users${params}`);
}

export async function getUserProfile(
  userId: string
): Promise<ProfilePageResponse> {
  return apiClient.get<ProfilePageResponse>(
    `/api/users/${encodeURIComponent(userId)}`
  );
}

export async function getFriends(): Promise<ListFriendsResponse> {
  return apiClient.get<ListFriendsResponse>("/api/friends");
}

export async function listFollows(): Promise<ListFollowsResponse> {
  return apiClient.get<ListFollowsResponse>("/api/follows");
}

export async function followUser(userId: string): Promise<FollowResponse> {
  return apiClient.post<FollowResponse>(
    `/api/follows/${encodeURIComponent(userId)}`,
    {}
  );
}

export async function unfollowUser(userId: string): Promise<{ deleted: true }> {
  return apiClient.delete<{ deleted: true }>(
    `/api/follows/${encodeURIComponent(userId)}`
  );
}

export async function addFriend(userId: string): Promise<FriendResponse> {
  return apiClient.post<FriendResponse>(
    `/api/friends/${encodeURIComponent(userId)}`,
    {}
  );
}

export async function acceptFriendRequest(
  userId: string
): Promise<FriendResponse> {
  return apiClient.post<FriendResponse>(
    `/api/friends/${encodeURIComponent(userId)}/accept`,
    {}
  );
}

export async function denyFriendRequest(
  userId: string
): Promise<{ denied: true }> {
  return apiClient.post<{ denied: true }>(
    `/api/friends/${encodeURIComponent(userId)}/deny`,
    {}
  );
}

export async function deleteFriend(userId: string): Promise<{ deleted: true }> {
  return apiClient.delete<{ deleted: true }>(
    `/api/friends/${encodeURIComponent(userId)}`
  );
}

export async function recordProfileView(
  userId: string
): Promise<{ counted: boolean }> {
  return apiClient.post<{ counted: boolean }>(
    `/api/users/${encodeURIComponent(userId)}/view`,
    {}
  );
}

export async function listAccountProfiles(): Promise<{ items: AccountProfileSummary[] }> {
  return apiClient.get<{ items: AccountProfileSummary[] }>("/api/account/profiles");
}

export async function createAccountProfile(payload: {
  username: string;
  displayName: string;
  profileType: ProfileType;
}): Promise<AccountProfileSummary> {
  return apiClient.post<AccountProfileSummary>("/api/account/profiles", payload);
}
